/* eslint-disable @typescript-eslint/naming-convention */
import { Directive, input, output, signal } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { HisTreeNode, HisTreeNodeData } from '../../model/his-tree.model';
import { TreeActionContext } from './his-tree-action-buttons.model';

interface State<T extends HisTreeNodeData = HisTreeNodeData> {
    cut: {
        readonly sourceNode: HisTreeNode<T> | null;
    } | null;
    copy: {
        readonly sourceNode: HisTreeNode<T> | null;
    } | null;
}

export interface TreeNodePasteEvent<T extends HisTreeNodeData = HisTreeNodeData> {
    /** The node to cut/copy */
    sourceNode: HisTreeNode<T>;
    /** The node on which the paste action is performed */
    targetNode: HisTreeNode<T>;
}

/**
 * Directive that enables cut/copy/paste state-management for a `p-treeTable` component.
 * 
 * Does not perform any actual cut/copy/paste operations on the tree data and instead only manages the state and emits
 * appropriate events for the host component to handle.
 * 
 * **1. Enable or disable**\
 *   Attached implicitly on `p-treeTable`. To disable/remove it, add `disableCopyCutDirective` on the table element.
 *
 * **2. Action button activation**\
 *   To show cut-related buttons in the row action component, include the `cut` default action in `TreeActionButtonsConfig`.
 *   When cut is enabled, paste and cancelCut are enabled implicitly as part of the same slot.
 *
 * **3. Automatic wiring with action buttons**\
 *   `copy`, `cut`, `paste`, and `cancelCut` actions are already connected to this directive’s state logic.
 *   No manual template wiring is required for those button actions.
 *
 * **4. Implement actual paste behavior**\
 *   Use the `pasteNode` output to perform the real tree mutation (move/copy node) in your feature code.
 *   This directive emits intent and context only.
 *
 * **5. Custom eligibility rules**\
 *   `canCut` and `canPaste` inputs let you provide custom predicates for whether an action is allowed.
 *   These predicates are evaluated consistently for every node using the current action context and control the 
 *   visibility of the action, not the render state.\
 *   (Render state is controlled internally through the `cutMode` state and the `sourceNode`.)
 */
@Directive( {
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'p-treeTable:not([disableCopyCutDirective])',
    exportAs: 'hisTreeCopyCut',
    standalone: true
} )
export class HisTreeCopyCutDirective<T extends HisTreeNodeData = HisTreeNodeData> {

    private readonly stateInternal = signal<State<T>>( { cut: null, copy: null } );
    readonly state = this.stateInternal.asReadonly();

    /** The root tree nodes assigned to the `p-treeTable` */
    readonly nodes = input<HisTreeNode<T>[] | TreeNode<T>[]>( [], { alias: 'value' } );
    /** Function evaluating if a node can be cut */
    readonly _canCutFn = input( ( ctx: TreeActionContext<T> ) => {
        if ( !ctx ) {
            return false;
        }
        return !this.nodes().includes( ctx.node ); // cant cut root nodes
    }, { alias: 'canCut' } );
    /** Function evaluating if a cut/copied node can be pasted on a node */
    readonly _canPasteFn = input( ( ctx: TreeActionContext<T> ) => {
        if ( !ctx ) {
            return false;
        }
        const state = this.state();
        if ( !state.cut?.sourceNode || !state.cut.sourceNode.parent || state.cut.sourceNode.parent === ctx.node ) {
            return false;
        }
        const immediateParent = state.cut.sourceNode.parent;
        if ( this.nodes().includes( immediateParent ) ) {
            return this.nodes().includes( ctx.node );
        }
        return immediateParent.parent.children.includes( ctx.node );
    }, { alias: 'canPaste' } );

    /** Event emitted when a node should be pasted */
    readonly pasteNode = output<TreeNodePasteEvent<T>>();

    /** Indicates whether the current node can be cut according to the custom eligibility rules */
    public get canCut() {
        return this._canCutFn();
    }

    /** Indicates whether the currently 'cut' node can be pasted according to the custom eligibility rules */
    public get canPaste() {
        return this._canPasteFn();
    }

    public cut( node: HisTreeNode<T> ): void {
        this.stateInternal.update( ( { copy } ) => ( { cut: { sourceNode: node }, copy } ) );
    }

    public copy( node: HisTreeNode<T> ): void {
        this.stateInternal.update( ( { cut } ) => ( { cut, copy: { sourceNode: node } } ) );
    }

    public paste( targetNode: HisTreeNode<T> ): void {
        const { cut, copy } = this.state();
        const sourceNode = cut?.sourceNode ?? null;
        if ( !sourceNode ) {
            return;
        }
        this.pasteNode.emit( { sourceNode, targetNode } );
        this.stateInternal.set( { cut: null, copy } );
    }

    public cancelCutMode(): void {
        this.stateInternal.update( ({copy}) => ( { cut: null, copy } ) );
    }

    public cancelCopyMode(): void {
        this.stateInternal.update( ({cut}) => ( { cut, copy: null } ) );
    }
}